import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Wifi, Coffee, Users, MonitorPlay, Star, Printer, ChefHat, Bike, Lock } from "lucide-react";
import api from "../lib/api";
import { eur, spaceTypeLabel } from "../lib/utils.helpers";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/vihkdp9y_IMG-20260908-WA0006.jpg";
const GALLERY = [
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/vihkdp9y_IMG-20260908-WA0006.jpg",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/gmnank3b_IMG-20260908-WA0003.jpg",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/glkjlq6v_IMG-20260908-WA0004.jpg",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/l2fndap9_IMG-20260908-WA0007.jpg",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/t0z7j30r_IMG-20260908-WA0008.jpg",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/fyzuoxt9_IMG-20260908-WA0009.jpg",
];

export default function Home() {
  const [spaces, setSpaces] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/spaces").then((r) => setSpaces(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-x grid lg:grid-cols-12 gap-10 py-16 lg:py-24 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <p className="text-[#2B8B8E] font-semibold tracking-wide text-2xl sm:text-3xl lg:text-4xl mb-6" data-testid="hero-overline">Premier espace de coworking de Petite Camargue</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] mb-6">
              Ici naissent les <span className="font-semibold italic text-[#2B8B8E]">plus belles énergies</span>.
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-4 leading-relaxed">
              Travaillez, recevez vos clients ou réunissez votre équipe dans un espace professionnel, convivial et entièrement équipé à Vauvert.
            </p>
            <p className="text-sm font-medium text-[#0D2C4D] mb-2">
              Open Space · Bureau privatif / Salle d'entretien · Salle de réunion
            </p>
            <p className="text-sm text-[#2B8B8E] font-semibold mb-8">
              À partir de 18 €/jour
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full px-8" data-testid="hero-cta-reserve">
                <Link to="/reserver">Réserver un espace <ArrowRight size={16} className="ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8" data-testid="hero-cta-pricing">
                <Link to="/tarifs">Découvrir les espaces</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <img src={HERO_IMG} alt="Synergie Workspace Vauvert" className="w-full h-[460px] lg:h-[560px] object-cover rounded-3xl shadow-xl" />
          </div>
        </div>
      </section>

      {/* SERVICES BAND */}
      <section className="border-y border-border bg-gradient-to-r from-[#2B8B8E]/8 via-[#0D2C4D]/5 to-[#2B8B8E]/8 py-10 overflow-hidden">
        <div className="container-x">
          <p className="text-center overline text-[#2B8B8E] mb-6">Inclus dans tous les espaces</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              { icon: Wifi, label: "Wi-Fi 1 Gbps" },
              { icon: Coffee, label: "Café" },
              { icon: Printer, label: "Imprimante laser" },
              { icon: MonitorPlay, label: "Salles visio" },
              { icon: ChefHat, label: "Cuisine partagée" },
              { icon: Bike, label: "Rangement vélos et trottinettes" },
              { icon: Lock, label: "Casiers Open Space" },
            ].map((s) => (
              <div key={s.label} className="group flex flex-col items-center text-center p-4 rounded-2xl bg-background/60 backdrop-blur-sm border border-[#2B8B8E]/10 hover:border-[#2B8B8E]/40 hover:bg-background transition-all duration-300 hover:-translate-y-1">
                <div className="w-11 h-11 rounded-full bg-[#2B8B8E]/10 group-hover:bg-[#2B8B8E] flex items-center justify-center mb-2 transition-colors">
                  <s.icon className="text-[#2B8B8E] group-hover:text-white transition-colors" size={20} />
                </div>
                <p className="text-xs font-semibold text-[#0D2C4D] leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPACES BENTO */}
      <section className="container-x py-20">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <p className="overline text-[#2B8B8E]">Nos espaces</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mt-2 max-w-2xl">Trois espaces pour s'adapter à votre rythme.</h2>
          </div>
          <Link to="/reserver" className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-[#2B8B8E] hover:underline">
            Réserver <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {spaces.map((s, i) => (
            <div
              key={s.id}
              className={`group relative overflow-hidden rounded-3xl border border-border bg-card hover:-translate-y-1 transition-all duration-300 ${i === 0 ? "md:col-span-7 row-span-2" : "md:col-span-5"}`}
              data-testid={`space-card-${s.type}`}
            >
              <img src={s.image_url} alt={s.name} className={`w-full ${i === 0 ? "h-[420px]" : "h-[200px]"} object-cover`} />
              <div className="p-6">
                <p className="overline text-[#0D2C4D]">{spaceTypeLabel(s.type)}</p>
                <h3 className="text-2xl font-semibold mt-2 mb-2">{s.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{s.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {s.equipments.slice(0, 4).map((e) => (
                    <span key={e} className="text-xs px-2 py-1 rounded-full bg-muted">{e}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm">
                    {s.price_full_day > 0 && <><span className="font-semibold text-lg">{eur(s.price_full_day)}</span><span className="text-muted-foreground text-xs"> / jour</span></>}
                  </p>
                  <Button asChild size="sm" className="bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full" data-testid={`book-space-${s.type}`}>
                    <Link to={`/reserver?space=${s.id}`}>Réserver</Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="container-x py-16">
        <p className="overline text-[#2B8B8E] mb-2">Galerie</p>
        <h2 className="text-3xl sm:text-4xl font-light mb-10">Visitez nos lieux.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {GALLERY.map((g) => (
            <img key={g} src={g} alt="Coworking" className="w-full h-44 md:h-56 object-cover rounded-2xl hover:scale-[1.02] transition-transform" />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-16">
        <div className="rounded-3xl bg-[#2C302E] dark:bg-[#232622] text-white p-10 md:p-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-3xl md:text-4xl font-light leading-tight mb-3">Prêt à essayer Synergie ?</h3>
            <p className="text-white/70">Réservez votre premier créneau dès maintenant. Annulation possible jusqu'à 24h avant.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Button asChild size="lg" className="bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full px-8" data-testid="cta-bottom-reserve">
              <Link to="/reserver">Réserver maintenant</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-8 border-white/20 text-white hover:bg-white/10" data-testid="cta-bottom-contact">
              <Link to="/contact">Nous contacter</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
